import React, {
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Box } from "@material-ui/core";
import { useDeviceTypes, LoadingPopup, B2BSpinner } from "halifax";
import { Route, RouteComponentProps } from "react-router-dom";
import clsx from "clsx";

import {
  CallState,
  CorePremiumStaysCheckout,
  IPerson,
  LodgingCollectionEnum,
  LodgingSelectionEnum,
  REVIEW_DETAILS_LC_CHECKOUT,
  REVIEW_DETAILS_PC_CHECKOUT,
  REVIEW_DETAILS_VR_CHECKOUT,
  RoomProduct,
  getPromotionTrackingProperties,
} from "redmond";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import { isCorpTenant, trackEngagementEvent } from "@capone/common";
import { PremierCollectionBookConnectorProps } from "./container";
import { PremierCollectionShopCallState } from "../shop/reducer";
import {
  selectLodging,
  selectRoomType,
  setPremierCollectionShopResults,
} from "../shop/actions/actions";
import {
  setFromDate,
  setLodgingSelection,
  setUntilDate,
} from "../search/actions/actions";
import { useVacationRentalBookListing } from "./utils/useVacationRentalBookListing";
import { actions as availabilityActions } from "../availability/actions";
import {
  BookingErrorModal,
  BookingInProgressModal,
  BookingSuccessModal,
} from "./components";
import {
  PATH_BOOK,
  PATH_BOOK_CONFIRMATION,
  PATH_VACATION_RENTALS_BOOK,
  PATH_VACATION_RENTALS_BOOK_CONFIRMATION,
} from "../../utils/paths";
import { goToShop } from "../shop/utils/queryStringHelpers";
import "./styles.scss";
import {
  HOTEL_CHECKOUT_TITLE,
  PORTAL_TITLE,
  VACATION_RENTAL_CHECKOUT_TITLE,
} from "./textConstants";
import { trackEvent } from "../../api/v0/analytics/trackEvent";
import {
  useExperiments,
  getExperimentVariant,
  getExperimentVariantJSON,
  TREES_MODAL_EXPERIMENT,
  AVAILABLE,
  TRAVEL_WALLET_OFFER_EXPERIMENT,
  TRAVEL_WALLET_CREDITS_EXPERIMENT,
  CREDIT_OFFER_STACKING_V1,
  TRAVEL_CREDIT_HISTORY_EXPERIMENT,
  ALLOWED_PAY_LATER_LODGINGS,
  AllowedPayLaterLodgingsConfig,
} from "../../context/experiments";
import { ClientContext } from "../../App";
import { MobileVacationRentalsBookWorkflow as ConsumerMobileVacationRentalsBookWorkflow } from "./components/MobileVacationRentalsBookWorkflow";
import { DesktopVacationRentalsBookWorkflow as ConsumerDesktopVacationRentalsBookWorkflow } from "./components/DesktopVacationRentalsBookWorkflow";
import { VRBookingErrorModal } from "./components/VRBookingErrorModal";
import { config } from "../../api/config";
import {
  DesktopVacationRentalsBookWorkflow as CorpDesktopVacationRentalsBookWorkflow,
  MobileVacationRentalsBookWorkflow as CorpMobileVacationRentalsBookWorkflow,
} from "./components/capone-corporate";

const CorpDesktopPremierCollectionBookWorkflow = React.lazy(
  () =>
    import("./components/capone-corporate/DesktopPremierCollectionBookWorkflow")
);
const CorpMobilePremierCollectionBookWorkflow = React.lazy(
  () =>
    import("./components/capone-corporate/MobilePremierCollectionBookWorkflow")
);
const ConsumerDesktopPremierCollectionBookWorkflow = React.lazy(
  () => import("./components/capone/DesktopPremierCollectionBookWorkflow")
);
const ConsumerMobilePremierCollectionBookWorkflow = React.lazy(
  () => import("./components/capone/MobilePremierCollectionBookWorkflow")
);

export interface IPremierCollectionBookProps
  extends PremierCollectionBookConnectorProps,
    RouteComponentProps {
  isVacationRentalBooking?: boolean;
  core?: CorePremiumStaysCheckout;
}

export const PremierCollectionBook = (props: IPremierCollectionBookProps) => {
  const {
    history,
    location,
    chosenProduct,
    selectedLodging,
    setPriceQuote,
    reviewPremierCollectionDetailsProperties,
    selectedHome,
    travelers,
    userPassengerCallState,
    viewedVacationRentalDetailsProperties,
    priceQuote,
    fetchApplicableTravelWalletItems,
    checkInDate,
    checkOutDate,
    tripTotal,
    accountReferenceIds,
    searchLocation,
    setAllowedPayLaterLodgingIds,
    clearSession,
    redoSearch,
    core,
    isVacationRentalBooking,
  } = props;
  const dispatch = useDispatch();
  const { matchesMobile, matchesDesktop } = useDeviceTypes();

  useEffect(() => {
    if (core == null) {
      return;
    }

    const fromDate = dayjs(core.search.dateRange.from).toDate();
    const untilDate = dayjs(core.search.dateRange.until).toDate();
    dispatch(setFromDate(fromDate));
    dispatch(setUntilDate(untilDate));
    dispatch(availabilityActions.setSearchedDates(fromDate, untilDate));
    if (core.lodgingSelection?.LodgingSelection === LodgingSelectionEnum.Place) {
      dispatch(setLodgingSelection(core.lodgingSelection));
    }
    dispatch(selectLodging(core.lodging));
    dispatch(
      setPremierCollectionShopResults({
        premierCollectionShopCallState: PremierCollectionShopCallState.Success,
        payload: {
          roomInfoProducts: [core.roomInfo],
          cancellationSummary: core.cancellationSummary,
        },
      })
    );
    dispatch(
      selectRoomType(
        0,
        core.roomInfo.products.findIndex(
          (product: RoomProduct) =>
            product.rateId?.value === core.roomProduct.rateId?.value
        )
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [primaryTraveler, setPrimaryTraveler] = useState<IPerson | undefined>(
    undefined
  );
  const [searchedPrimaryTraveler, setSearchedPrimaryTraveler] = useState(false);
  const [EDSEventSent, setEDSEventSent] = useState<boolean>(false);

  const userInfo = useContext(ClientContext).sessionInfo?.userInfo;

  const expState = useExperiments();

  const {
    isListingReady,
    isListingLoading: isVacationRentalBookLoading,
  } = useVacationRentalBookListing({
    isVacationRentalBooking: isVacationRentalBooking === true,
    history,
    location,
    selectedHome,
    redoSearch,
  });

  const allowedPayLaterLodgingsConfig = getExperimentVariantJSON(
    expState.experiments,
    ALLOWED_PAY_LATER_LODGINGS
  ) as AllowedPayLaterLodgingsConfig | null;

  const treesModalExperiment = getExperimentVariant(
    expState.experiments,
    TREES_MODAL_EXPERIMENT
  );
  const isTreesModalExperiment = useMemo(
    () => treesModalExperiment === AVAILABLE,
    [treesModalExperiment]
  );

  useEffect(() => {
    setAllowedPayLaterLodgingIds(allowedPayLaterLodgingsConfig?.data?.lodgingIds ?? []);
  }, [setAllowedPayLaterLodgingIds, allowedPayLaterLodgingsConfig]);

  const travelWalletOffer = getExperimentVariant(
    expState.experiments,
    TRAVEL_WALLET_OFFER_EXPERIMENT
  );
  const isTravelWalletOfferExperiment = React.useMemo(
    () => travelWalletOffer === AVAILABLE,
    [travelWalletOffer]
  );

  const travelWalletCreditsExperiment = getExperimentVariant(
    expState.experiments,
    TRAVEL_WALLET_CREDITS_EXPERIMENT
  );
  const isTravelWalletCreditsExperiment = React.useMemo(
    () => travelWalletCreditsExperiment === AVAILABLE,
    [travelWalletCreditsExperiment]
  );

  const creditAndOfferStackingExperimentV1 = getExperimentVariant(
    expState.experiments,
    CREDIT_OFFER_STACKING_V1
  );
  const isCreditAndOfferStackingExperimentV1 = useMemo(
    () => creditAndOfferStackingExperimentV1 === AVAILABLE,
    [creditAndOfferStackingExperimentV1]
  );

  const travelCreditHistoryExperiment = getExperimentVariant(
    expState.experiments,
    TRAVEL_CREDIT_HISTORY_EXPERIMENT
  );
  const isTravelCreditHistoryExperiment = useMemo(
    () => travelCreditHistoryExperiment === AVAILABLE,
    [travelCreditHistoryExperiment]
  );

  const DesktopPremierCollectionBookWorkflow = isCorpTenant(config.TENANT)
    ? CorpDesktopPremierCollectionBookWorkflow
    : ConsumerDesktopPremierCollectionBookWorkflow;

  const MobilePremierCollectionBookWorkflow = isCorpTenant(config.TENANT)
    ? CorpMobilePremierCollectionBookWorkflow
    : ConsumerMobilePremierCollectionBookWorkflow;

  const DesktopVacationRentalsBookWorkflow = isCorpTenant(config.TENANT)
    ? CorpDesktopVacationRentalsBookWorkflow
    : ConsumerDesktopVacationRentalsBookWorkflow;

  const MobileVacationRentalsBookWorkflow = isCorpTenant(config.TENANT)
    ? CorpMobileVacationRentalsBookWorkflow
    : ConsumerMobileVacationRentalsBookWorkflow;

  useEffect(() => {
    if (
      isVacationRentalBooking !== true &&
      core == null &&
      !selectedHome &&
      (!chosenProduct || !selectedLodging)
    ) {
      goToShop({ history });
    }
  }, [
    selectedHome,
    chosenProduct,
    selectedLodging,
    core,
    isVacationRentalBooking,
  ]);

  useEffect(() => {
    if (!!chosenProduct && !!priceQuote) {
      if (
        chosenProduct.tripTotal.fiat.value !==
        priceQuote.pricing.tripTotal.fiat.value
      ) {
        isTravelWalletOfferExperiment &&
          fetchApplicableTravelWalletItems(selectedHome !== null);
      }
    }
  }, [chosenProduct, priceQuote, selectedHome]);

  useEffect(() => {
    setPriceQuote(null, null);
    setTimeout(() => window.scrollTo(0, 0), 0);
  }, [location.pathname]);

  useEffect(() => {
    document.title = selectedHome
      ? VACATION_RENTAL_CHECKOUT_TITLE
      : HOTEL_CHECKOUT_TITLE;
    selectedHome
      ? trackEvent({
          eventName: REVIEW_DETAILS_VR_CHECKOUT,
          properties: {
            ...viewedVacationRentalDetailsProperties.properties,
          },
          encryptedProperties: [
            ...viewedVacationRentalDetailsProperties.encryptedProperties,
          ],
        })
      : trackEvent({
          eventName:
            selectedLodging?.lodgingCollection ===
            LodgingCollectionEnum.Lifestyle
              ? REVIEW_DETAILS_LC_CHECKOUT
              : REVIEW_DETAILS_PC_CHECKOUT,
          properties: {
            ...reviewPremierCollectionDetailsProperties.properties,
          },
          encryptedProperties: [
            ...reviewPremierCollectionDetailsProperties.encryptedProperties,
          ],
        });
    return () => {
      document.title = PORTAL_TITLE;
    };
  }, []);

  useEffect(() => {
    return () => {
      const resetCheckoutStates = () => {
        // note: close off any opening session upon leaving the book page
        clearSession();
        // note: calling redoSearch (on dismount) erases any checkout states upon leaving the book page
        redoSearch();
      };

      resetCheckoutStates();
    };
  }, []);

  useEffect(() => {
    if (userPassengerCallState === CallState.Success) {
      const savedPrimaryTraveler = travelers.find(
        (t) =>
          t.givenName === userInfo?.firstName && t.surname == userInfo.lastName
      );
      setSearchedPrimaryTraveler(true);
      if (!searchedPrimaryTraveler) {
        setPrimaryTraveler(savedPrimaryTraveler);
      }
    }
  }, [travelers, userPassengerCallState, searchedPrimaryTraveler]);

  useEffect(() => {
    if (
      !EDSEventSent &&
      selectedLodging &&
      location.pathname.startsWith(PATH_BOOK) &&
      !!tripTotal
    ) {
      trackEngagementEvent({
        event: {
          event_type: "hotel_checkout",
          ...getPromotionTrackingProperties(selectedLodging),
          lodging_id: selectedLodging?.lodging.id,
          lodging_name: selectedLodging?.lodging.name,
          lodging_type: selectedLodging?.lodgingCollection,
          advance: dayjs(checkInDate).diff(dayjs(), "day"),
          lodging_address:
            selectedLodging?.lodging?.address &&
            "line1" in selectedLodging?.lodging?.address
              ? selectedLodging?.lodging.address.line1
              : undefined,
          lodging_city: selectedLodging?.lodging.city,
          lodging_country: selectedLodging?.lodging.country,
          coordinates: selectedLodging?.lodging.location.coordinates,
          length_of_stay: dayjs(checkOutDate).diff(checkInDate, "day"),
          market_name_full: searchLocation?.label || "", // TODO: Use real market name in place of search term when available: https://hopper-jira.atlassian.net/browse/CMKT-3678
          owed_overall_including_taxes_fees: tripTotal?.fiat,
          check_in_date_timestamp: checkInDate?.getTime(),
          check_out_date_timestamp: checkOutDate?.getTime(),
          account_reference_ids: accountReferenceIds,
        },
      });

      setEDSEventSent(true);
    }
  }, [selectedLodging, tripTotal]);

  return (
    <Box
      className={clsx(
        "hotel-book-root",
        { confirm: location.pathname === PATH_BOOK_CONFIRMATION },
        { mobile: matchesMobile }
      )}
    >
      <Route path={PATH_BOOK} exact>
        <>
          <Box className="hotel-book-container">
            {matchesDesktop && (
              <Suspense>
                <DesktopPremierCollectionBookWorkflow
                  isTreesModalExperiment={isTreesModalExperiment}
                  isTravelWalletOfferExperiment={isTravelWalletOfferExperiment}
                  isCreditAndOfferStackingExperimentV1={
                    isCreditAndOfferStackingExperimentV1
                  }
                  isTravelCreditHistoryExperiment={
                    isTravelCreditHistoryExperiment
                  }
                />
              </Suspense>
            )}
            {matchesMobile && (
              <Suspense>
                <MobilePremierCollectionBookWorkflow
                  isTreesModalExperiment={isTreesModalExperiment}
                  isTravelWalletOfferExperiment={isTravelWalletOfferExperiment}
                  isTravelWalletCreditsExperiment={
                    isTravelWalletCreditsExperiment
                  }
                  isCreditAndOfferStackingExperimentV1={
                    isCreditAndOfferStackingExperimentV1
                  }
                  isTravelCreditHistoryExperiment={
                    isTravelCreditHistoryExperiment
                  }
                />
              </Suspense>
            )}
          </Box>
          <BookingErrorModal />
          <BookingInProgressModal />
        </>
      </Route>
      <Route path={PATH_VACATION_RENTALS_BOOK} exact>
        <Box className="vacation-rental-book-container">
          {matchesDesktop && isListingReady && (
            <DesktopVacationRentalsBookWorkflow
              isTreesModalExperiment={isTreesModalExperiment}
              isTravelWalletOfferExperiment={isTravelWalletOfferExperiment}
              isCreditAndOfferStackingExperimentV1={
                isCreditAndOfferStackingExperimentV1
              }
              isTravelCreditHistoryExperiment={isTravelCreditHistoryExperiment}
              primaryTraveler={primaryTraveler}
            />
          )}
          {matchesMobile && isListingReady && (
            <MobileVacationRentalsBookWorkflow
              isTreesModalExperiment={isTreesModalExperiment}
              isTravelWalletOfferExperiment={isTravelWalletOfferExperiment}
              isTravelWalletCreditsExperiment={isTravelWalletCreditsExperiment}
              isCreditAndOfferStackingExperimentV1={
                isCreditAndOfferStackingExperimentV1
              }
              isTravelCreditHistoryExperiment={isTravelCreditHistoryExperiment}
              primaryTraveler={primaryTraveler}
            />
          )}
        </Box>
        <VRBookingErrorModal />
        <BookingInProgressModal isBookingHome />
        <LoadingPopup
          indicatorSize="small"
          indicator={B2BSpinner}
          open={isVacationRentalBookLoading}
          popupSize="small"
          message="Checking availability"
        />
      </Route>
      <Route
        path={[PATH_BOOK_CONFIRMATION, PATH_VACATION_RENTALS_BOOK_CONFIRMATION]}
      >
        <BookingSuccessModal isTreesModalExperiment={isTreesModalExperiment} />
      </Route>
    </Box>
  );
};
